import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios, { AxiosResponse } from 'axios';
import { SavedTimesheet, UserWorkedHoursWithStatistics } from 'src/models/timesheet.model';

type InitialState = {
  loading: boolean;
  success: boolean;
  failure: boolean;
  userTimesheets: UserWorkedHoursWithStatistics;
};

const INITIAL_STATE: InitialState = {
  loading: false,
  success: false,
  failure: false,
  userTimesheets: null as unknown as UserWorkedHoursWithStatistics,
};

export const searchUserTimesheets = createAsyncThunk(
  'user-timesheets/post',
  async (data: { userId: number; period: string }, { rejectWithValue }) => {
    try {
      const response: AxiosResponse<UserWorkedHoursWithStatistics> = await axios.post(`/api/user-timesheets/${data.userId}`, data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

const searchUserTimesheetSlice = createSlice({
  name: 'searchUserTimesheet',
  initialState: INITIAL_STATE,
  reducers: {
    resetSearchUserTimesheet: () => {
      return INITIAL_STATE;
    },
    addCell: (state, action: PayloadAction<{ key: string; cell: SavedTimesheet }>) => {
      const { key, cell } = action.payload;
      const hoursToAdd = cell.hours || 0;

      if (cell.commission.commissionName !== 'smartworking') {
        state.userTimesheets.statistics.total += hoursToAdd;
        state.userTimesheets.statistics.totalCommission[key] = (state.userTimesheets.statistics.totalCommission[key] || 0) + hoursToAdd;
        state.userTimesheets.statistics.totalCommission[cell.commission.commissionName] =
          (state.userTimesheets.statistics.totalCommission[cell.commission.commissionName] || 0) + hoursToAdd;
      } else {
        state.userTimesheets.statistics.totalCommission['smartworking'] =
          (state.userTimesheets.statistics.totalCommission['smartworking'] || 0) + hoursToAdd;
      }

      state.userTimesheets.workedHours[key] = cell;
    },
    updateCell: (state, action: PayloadAction<{ key: string; cell: SavedTimesheet }>) => {
      const { key, cell } = action.payload;

      if (state.userTimesheets.workedHours[key]) {
        const existingHours = state.userTimesheets.workedHours[key].hours || 0;
        const newHours = cell.hours || 0;

        state.userTimesheets.statistics.total -= existingHours;
        state.userTimesheets.statistics.total += newHours;

        if (state.userTimesheets.workedHours[key].commission.commissionName !== 'smartworking') {
          state.userTimesheets.statistics.totalCommission[key] =
            (state.userTimesheets.statistics.totalCommission[key] || 0) - existingHours + newHours;
          state.userTimesheets.statistics.totalCommission[cell.commission.commissionName] =
            (state.userTimesheets.statistics.totalCommission[cell.commission.commissionName] || 0) - existingHours + newHours;
        } else {
          state.userTimesheets.statistics.totalCommission['smartworking'] -= existingHours;
          state.userTimesheets.statistics.totalCommission['smartworking'] += newHours;
        }

        state.userTimesheets.workedHours[key] = cell;
      }
    },

    removeCell: (state, action: PayloadAction<{ key: string; commissionName: string }>) => {
      const { key, commissionName } = action.payload;

      if (state.userTimesheets.workedHours[key]) {
        const hoursToRemove = state.userTimesheets.workedHours[key].hours || 0;
        if (state.userTimesheets.workedHours[key].commission.commissionName !== 'smartworking') {
          state.userTimesheets.statistics.totalCommission[key] =
            (state.userTimesheets.statistics.totalCommission[key] || 0) - hoursToRemove;

          state.userTimesheets.statistics.total -= hoursToRemove;
          state.userTimesheets.statistics.totalCommission[commissionName] -= hoursToRemove;
        } else {
          state.userTimesheets.statistics.totalCommission['smartworking'] -= hoursToRemove;
        }

        delete state.userTimesheets.statistics.totalCommission[key];

        delete state.userTimesheets.workedHours[key];
      }
    },
  },
  extraReducers: builder => {
    builder
      .addCase(searchUserTimesheets.pending, (state): InitialState => {
        return {
          ...state,
          loading: true,
          success: false,
          failure: false,
        };
      })
      .addCase(searchUserTimesheets.fulfilled, (state, action: PayloadAction<UserWorkedHoursWithStatistics>): InitialState => {
        return {
          ...state,
          loading: false,
          success: true,
          failure: false,
          userTimesheets: action.payload,
        };
      })
      .addCase(searchUserTimesheets.rejected, (state): InitialState => {
        return {
          ...state,
          loading: false,
          success: false,
          failure: true,
        };
      });
  },
});

export const { resetSearchUserTimesheet, addCell, updateCell, removeCell } = searchUserTimesheetSlice.actions;

export default searchUserTimesheetSlice.reducer;
